/**
 * FilterV2: flattened run filter format for UI/viewSpec storage.
 *
 * AND/OR is expressed via an inline `connector` property on each item
 * (except the first). `connector` describes how this item connects
 * to the *previous* item. AND has higher precedence than OR.
 *
 * @see https://www.notion.so/wandbai/And-Or-Technical-Doc-319e2f5c7ef3801aac1ac2b7eaec08d8
 *  for detailed technical design and implementation details
 *
 * The tree representation is derived only when needed for backend queries
 * (toMongo). All UI edits operate on the flat list directly.
 *
 * @see https://react-querybuilder.js.org/docs/components/querybuilder#independent-combinators
 *   for the same "independent combinators" data model that validates this approach.
 */

import {recordRumAction} from './datadog/datadogRum';
import {activeFilterCount, fixRunFilter, isIndividual} from './filters';
import type * as FilterTypes from './filterTypes';
import type {
  Filter,
  GroupFilter,
  IndividualFilter,
  RootFilter,
} from './filterTypes';
import type * as RunTypes from './runTypes';

export const FILTER_FORMAT_V2 = 'filterV2' as const;

export const EMPTY_QUERY_FILTER_V2: Filter<RunTypes.Key> = {
  op: 'AND',
  filters: [],
};

export type ConnectorOp = 'AND' | 'OR';

/**
 * A single filter condition with an inline connector to the previous item.
 * `connector` is undefined on the first item in any list.
 *
 * Extends IndividualFilter directly so the two types never drift apart.
 */
export type FlatIndividualItem = IndividualFilter<RunTypes.Key> & {
  connector?: ConnectorOp;
};

/**
 * A nested group (parentheses). Its internal filters follow the same
 * precedence rules; the group as a whole is treated as a single item
 * by its parent.
 */
export interface FilterV2Group {
  filters: FilterV2Item[];
  connector?: ConnectorOp;
  disabled?: boolean;
}

export type FilterV2Item = FlatIndividualItem | FilterV2Group;

/** Root object stored in the viewSpec. */
export interface FilterV2Root {
  filterFormat: typeof FILTER_FORMAT_V2;
  filters: FilterV2Item[];
}

/**
 * Storage format for run-set filters.
 *
 * In-memory we always operate on `FilterV2Root`. Legacy `RootFilter<K>` may
 * appear in raw view-spec JSON loaded from the backend; it's migrated to
 * V2 on read (see `filtersFromJSON` and `migrateReportRunSetFilters`).
 *
 * The legacy variant is retained in this union so that the migration boundary
 * can still type-check the input. After migration, callers can assume the
 * value is always `FilterV2Root`.
 */
export type StoredFilter = FilterV2Root | RootFilter<RunTypes.Key>;

/** Empty FilterV2 root. */
export const EMPTY_STORED_FILTER_V2: FilterV2Root = {
  filterFormat: FILTER_FORMAT_V2,
  filters: [],
};

export function emptyStoredFilter(): FilterV2Root {
  return EMPTY_STORED_FILTER_V2;
}

export function emptyQueryFilter(): FilterTypes.Filter<RunTypes.Key> {
  return EMPTY_QUERY_FILTER_V2;
}

export function isFilterV2Group(item: FilterV2Item): item is FilterV2Group {
  return item != null && 'filters' in item && Array.isArray(item.filters);
}

export function isFilterV2(
  value: FilterV2Root | RootFilter<FilterTypes.FilterKey> | null | undefined
): value is FilterV2Root {
  return (
    value != null &&
    typeof value === 'object' &&
    'filterFormat' in value &&
    value.filterFormat === FILTER_FORMAT_V2
  );
}

export function getConnector(item: FilterV2Item): ConnectorOp | undefined {
  return item.connector;
}

export function withConnector(
  item: FilterV2Item,
  connector: ConnectorOp
): FilterV2Item {
  return {...item, connector};
}

export function withoutConnector(item: FilterV2Item): FilterV2Item {
  const {connector: _, ...rest} = item;
  return rest;
}

/**
 * True if this item is the UI-only empty placeholder (key name is blank
 * and item is disabled or has no value). These are never sent to the backend.
 */
export function isEmptyPlaceholderItem(item: FlatIndividualItem): boolean {
  const k = item.key;
  return (
    k != null &&
    k.section === 'run' &&
    k.name === '' &&
    (item.disabled === true || item.value === '')
  );
}

/*
 * Derive tree (flat → tree with AND > OR precedence)
 */

/**
 * Split a flat filter list into segments at OR boundaries.
 * An item whose connector is 'OR' starts a new segment.
 *
 * Example:
 *   e.g. example filter: (state = 'finished' AND name = 'x') OR user = 'taylor' OR id = '1989'
 *   Input: [
 *     { key: "state", op: "=", value: "finished" },
 *     { key: "name",  op: "=", value: "x",      connector: "AND" },
 *     { key: "user",  op: "=", value: "taylor",  connector: "OR" },
 *     { key: "id",    op: "=", value: "1989",    connector: "OR" },
 *   ]
 *
 *   Output: [
 *     [{ key: "state", ... }, { key: "name", ... }],
 *     [{ key: "user", ... }],
 *     [{ key: "id", ... }],
 *   ]
 *
 * The first two items share an AND connector, so they stay in the same segment.
 * Each OR starts a new segment. Each segment is then treated as AND-connected
 * by deriveTree.
 */
function splitOnOR(filters: FilterV2Item[]): FilterV2Item[][] {
  if (filters.length === 0) {
    return [];
  }
  const segments: FilterV2Item[][] = [];
  let current: FilterV2Item[] = [];
  for (const item of filters) {
    if (getConnector(item) === 'OR' && current.length > 0) {
      segments.push(current);
      current = [];
    }
    current.push(item);
  }
  if (current.length > 0) {
    segments.push(current);
  }
  return segments;
}

function hasValidKey(item: FlatIndividualItem): boolean {
  const k = item.key;
  return k != null && k.section != null && k.name != null && k.name !== '';
}

/**
 * True if an item contributes to the derived tree — either as a clause or as a
 * grouping boundary. deriveTree drops everything else *before* splitting on OR,
 * so a dropped row cannot leak its connector into the surrounding rows. e.g.
 * `A AND B, C(OR, disabled), D AND` derives to `A AND B AND D`, not
 * `(A AND B) OR D` — disabling C removes both its clause and its OR split.
 *
 * A leaf contributes when it is enabled and has a valid key. A group always
 * contributes when enabled; if it turns out to be empty (or all its children
 * are dropped) it collapses to null during recursion in segmentToNode.
 */
function contributesToDerivedTree(item: FilterV2Item): boolean {
  if (item.disabled) {
    return false;
  }
  if (isFilterV2Group(item)) {
    return true;
  }
  return hasValidKey(item);
}

/**
 * Convert a FlatIndividualItem to an IndividualFilter by stripping `connector`.
 * Since FlatIndividualItem extends IndividualFilter, this is the only difference.
 */
function flatToIndividual(
  item: FlatIndividualItem
): IndividualFilter<RunTypes.Key> {
  const {connector: _, ...filter} = item;
  return filter;
}

/**
 * Convert one item (leaf or group) into a tree node. Groups recurse via
 * `deriveTreeInner` and may collapse to null (empty / all children dropped).
 * Leaves have already passed `contributesToDerivedTree`, so they always yield
 * a node.
 */
function itemToNode(
  item: FilterV2Item,
  deriveTreeInner: (node: {
    filters: FilterV2Item[];
  }) => Filter<RunTypes.Key> | null
): Filter<RunTypes.Key> | null {
  return isFilterV2Group(item) ? deriveTreeInner(item) : flatToIndividual(item);
}

/**
 * Convert one AND-connected segment into a tree node.
 * Recurses into nested groups via `deriveTreeInner`.
 *
 * Every item in `segment` has already passed `contributesToDerivedTree` (the
 * filter deriveTree applies before splitting), so disabled/empty leaves never
 * reach here — only groups can still collapse to null during recursion.
 */
function segmentToNode(
  segment: FilterV2Item[],
  deriveTreeInner: (node: {
    filters: FilterV2Item[];
  }) => Filter<RunTypes.Key> | null
): Filter<RunTypes.Key> | null {
  if (segment.length === 0) {
    return null;
  }
  /*
   * Single item — return it directly rather than wrapping in an AND node.
   * e.g. for `A OR B`, splitOnOR gives segments [A] and [B]. Without this,
   * we'd produce { op:'OR', filters:[{ op:'AND', filters:[A] }, { op:'AND', filters:[B] }] }
   * instead of the cleaner { op:'OR', filters:[A, B] }.
   */
  if (segment.length === 1) {
    return itemToNode(segment[0], deriveTreeInner);
  }
  const children: Array<Filter<RunTypes.Key>> = [];
  for (const item of segment) {
    const node = itemToNode(item, deriveTreeInner);
    if (node != null) {
      children.push(node);
    }
  }
  if (children.length === 0) {
    return null;
  }
  if (children.length === 1) {
    return children[0];
  }
  return {op: 'AND', filters: children};
}

/**
 * Derive a boolean tree from a FilterV2 node.
 *
 * Precedence (highest → lowest):
 *   1. Groups (parentheses)
 *   2. AND
 *   3. OR
 *
 * Algorithm:
 *   1. Split the flat filter list on OR boundaries
 *   2. Treat each resulting segment as AND-connected
 *   3. Recursively derive trees for any nested groups
 *   4. If more than one segment, combine with OR
 */
export function deriveTree(
  node: FilterV2Root | FilterV2Group | {filters: FilterV2Item[]}
): Filter<RunTypes.Key> | null {
  const {filters} = node;
  if (!Array.isArray(filters)) {
    recordMalformedFilterDropped(node);
    return null;
  }
  if (filters.length === 0) {
    return null;
  }

  // Drop non-contributing rows (disabled / empty) before splitting so their
  // connectors cannot re-parent the surviving rows. See contributesToDerivedTree.
  const segments = splitOnOR(filters.filter(contributesToDerivedTree));
  const segmentNodes: Array<Filter<RunTypes.Key>> = [];

  for (const segment of segments) {
    const treeNode = segmentToNode(segment, deriveTree);
    if (treeNode != null) {
      segmentNodes.push(treeNode);
    }
  }

  if (segmentNodes.length === 0) {
    return null;
  }
  if (segmentNodes.length === 1) {
    return segmentNodes[0];
  }
  return {op: 'OR', filters: segmentNodes};
}

/*
 * Legacy tree → FilterV2 migration
 */

/**
 * Run-set filters have been seen with a node that is neither a leaf nor a
 * group (no `key`, non-array `filters`), which crashed Vega2 panels with
 * "filters is not iterable", and with a root that is not an object at all
 * (e.g. a JSON-encoded Mongo string). Such a value can't express a
 * condition, so it is dropped. Report its shape (never its values) so the
 * writer can be found.
 */
function recordMalformedFilterDropped(node: unknown): void {
  if (node == null || typeof node !== 'object') {
    recordRumAction('filters.malformed_dropped', {
      nodeType: node === null ? 'null' : typeof node,
    });
    return;
  }
  recordRumAction('filters.malformed_dropped', {
    keys: Object.keys(node),
    filtersType:
      'filters' in node
        ? node.filters === null
          ? 'null'
          : typeof node.filters
        : 'missing',
  });
}

/**
 * Recursively flatten a legacy Filter into FlatIndividualItems. Individual
 * filters are returned as-is; group filters are expanded recursively with
 * inner connectors preserved and the parent's op used at group boundaries.
 */
export function flattenLegacyFilter(
  filter: FilterTypes.Filter<RunTypes.Key>
): FlatIndividualItem[] {
  if (isIndividual(filter)) {
    return [filter];
  }
  const group = filter as GroupFilter<RunTypes.Key>;
  if (!Array.isArray(group.filters)) {
    recordMalformedFilterDropped(filter);
    return [];
  }
  const items: FlatIndividualItem[] = [];
  for (const child of group.filters) {
    const childItems = flattenLegacyFilter(child);
    for (const item of childItems) {
      if (items.length > 0 && item.connector == null) {
        items.push({...item, connector: group.op});
      } else {
        items.push(item);
      }
    }
  }
  return items;
}

/**
 * Convert a legacy filter into the flat FilterV2 format.
 * Used on read when opening a workspace that has old-style filters.
 */
export function migrateLegacyFiltersToV2(
  filter: FilterTypes.Filter<RunTypes.Key>
): FilterV2Root {
  if (filter == null || typeof filter !== 'object') {
    recordMalformedFilterDropped(filter);
    return EMPTY_STORED_FILTER_V2;
  }
  const items = flattenLegacyFilter(fixRunFilter(filter));
  return items.length > 0
    ? {filterFormat: FILTER_FORMAT_V2, filters: items}
    : EMPTY_STORED_FILTER_V2;
}

/*
 * CRUD helpers
 * Insert, remove, and update filters in a FilterV2Item[] array.
 * Work for both root-level (FilterV2Root) and group-level (FilterV2Group) edits.
 */

/**
 * Insert `item` at `index` in `filters`. Returns a new array.
 *
 * The connector lives on the item itself — callers set it before passing
 * the item in. When inserting at index 0, the connector is stripped from
 * the new first item (first items never have one), and the displaced
 * old-first-item gets a default 'AND' connector.
 */
function spliceFilter(
  filters: FilterV2Item[],
  index: number,
  item: FilterV2Item
): FilterV2Item[] {
  const result = [...filters];
  const newItem = index > 0 ? item : withoutConnector(item);
  result.splice(index, 0, newItem);
  // The old first item never had a connector (nothing before it). Now it's
  // at index 1, so it needs one to describe how it connects to the new first item.
  if (index === 0 && result.length > 1 && !getConnector(result[1])) {
    result[1] = withConnector(result[1], 'AND');
  }
  return result;
}

/** Remove the item at `index` from `filters`. Returns a new array. */
function removeFilter(filters: FilterV2Item[], index: number): FilterV2Item[] {
  const result = filters.filter((_, i) => i !== index);
  // If the removed item was first, strip the connector from the new first item.
  if (index === 0 && result.length > 0) {
    result[0] = withoutConnector(result[0]);
  }
  return result;
}

/**
 * Replace the item at `filterIndex` with `item`. The item's connector is
 * used as-is, except at index 0 where it's always stripped (first items
 * never have a connector).
 */
function updateFilter(
  filters: FilterV2Item[],
  filterIndex: number,
  item: FilterV2Item
): FilterV2Item[] {
  const result = [...filters];
  result[filterIndex] = filterIndex > 0 ? item : withoutConnector(item);
  return result;
}

/* Root-level wrappers */

export function insertFilterV2At(
  root: FilterV2Root,
  index: number,
  item: FilterV2Item
): FilterV2Root {
  return {
    filterFormat: FILTER_FORMAT_V2,
    filters: spliceFilter(root.filters, index, item),
  };
}

export function removeFilterV2At(
  root: FilterV2Root,
  index: number
): FilterV2Root {
  return {
    filterFormat: FILTER_FORMAT_V2,
    filters: removeFilter(root.filters, index),
  };
}

export function updateFilterV2RootAt(
  root: FilterV2Root,
  filterIndex: number,
  item: FilterV2Item
): FilterV2Root {
  return {
    filterFormat: FILTER_FORMAT_V2,
    filters: updateFilter(root.filters, filterIndex, item),
  };
}

/* Group-level wrappers */

export function insertFilterV2GroupAt(
  group: FilterV2Group,
  index: number,
  item: FilterV2Item
): FilterV2Group {
  return {
    filters: spliceFilter(group.filters, index, item),
    disabled: group.disabled,
  };
}

export function removeFilterV2GroupAt(
  group: FilterV2Group,
  index: number
): FilterV2Group {
  return {
    filters: removeFilter(group.filters, index),
    disabled: group.disabled,
  };
}

export function updateFilterV2GroupAt(
  group: FilterV2Group,
  filterIndex: number,
  item: FilterV2Item
): FilterV2Group {
  return {
    filters: updateFilter(group.filters, filterIndex, item),
    disabled: group.disabled,
  };
}

/*
 * Path-based group navigation
 */

/**
 * Get the FilterV2Group at `path` (indices into nested filters).
 * Currently the UI only supports 1 level of nesting (path length <= 1),
 * but this handles arbitrary depth so deeper nesting works if we add it later.
 */
export function getFilterV2GroupAtPath(
  root: FilterV2Root,
  path: number[]
): FilterV2Group | undefined {
  if (path.length === 0) {
    return {filters: root.filters};
  }
  let current: FilterV2Item[] = root.filters;
  for (let i = 0; i < path.length; i++) {
    const item = current[path[i]];
    if (item == null || !isFilterV2Group(item)) {
      return undefined;
    }
    if (i === path.length - 1) {
      return item;
    }
    current = item.filters;
  }
  return undefined;
}

/**
 * Replace the group at `path` with `newGroup`.
 * Preserves the existing connector on the replaced group.
 */
export function setFilterV2GroupAtPath(
  root: FilterV2Root,
  path: number[],
  newGroup: FilterV2Group
): FilterV2Root {
  if (path.length === 0) {
    return {filterFormat: FILTER_FORMAT_V2, filters: newGroup.filters};
  }

  function replaceAtLevel(
    items: FilterV2Item[],
    remaining: number[]
  ): FilterV2Item[] {
    const [indexAtThisLevel, ...deeperPath] = remaining;
    const item = items[indexAtThisLevel];
    if (!isFilterV2Group(item)) {
      return items;
    }
    const updatedItems = [...items];
    if (deeperPath.length === 0) {
      // Arrived at the target — replace the group, preserving its connector.
      const existingConnector = getConnector(item);
      const replaced: FilterV2Group = {
        filters: newGroup.filters,
        disabled: newGroup.disabled,
      };
      updatedItems[indexAtThisLevel] =
        existingConnector != null
          ? withConnector(replaced, existingConnector)
          : replaced;
    } else {
      // Not at the target yet — recurse into this group's children.
      const updatedChildren = replaceAtLevel(item.filters, deeperPath);
      const updatedGroup: FilterV2Group = {
        filters: updatedChildren,
        disabled: item.disabled,
      };
      const existingConnector = getConnector(item);
      updatedItems[indexAtThisLevel] =
        existingConnector != null
          ? withConnector(updatedGroup, existingConnector)
          : updatedGroup;
    }
    return updatedItems;
  }

  return {
    filterFormat: FILTER_FORMAT_V2,
    filters: replaceAtLevel(root.filters, path),
  };
}

/*
 * Adapter helpers (bridging stored ↔ UI ↔ backend)
 */

/**
 * UI-only placeholder injected when the filter list would otherwise be empty,
 * so the UI always has at least one row to render.
 */
export const EMPTY_PLACEHOLDER_FILTER: FlatIndividualItem = {
  key: {section: 'run', name: ''},
  op: '=',
  value: '',
  disabled: true,
};

/**
 * Convert stored filters to a query-ready Filter tree for backend consumers.
 * Derives a tree from FilterV2 on the fly. Legacy `RootFilter<K>` values
 * (which may slip through from un-migrated view-spec data) are migrated
 * to V2 first.
 *
 * Always returns a non-null Filter. Empty V2 filters become
 * `EMPTY_QUERY_FILTER_V2` (`{op:'AND',filters:[]}`) — backend treats as match-all.
 *
 * Callers that need to combine the result with additional filters (e.g.
 * group selections) should use `Filter.And([result, ...extras])` themselves.
 */
export function getFiltersForQuery(
  stored: FilterV2Root | RootFilter<RunTypes.Key>
): Filter<RunTypes.Key> {
  const v2 = isFilterV2(stored) ? stored : migrateLegacyFiltersToV2(stored);
  return deriveTree(v2) ?? EMPTY_QUERY_FILTER_V2;
}

/**
 * Return stored filters as FilterV2Root for the UI.
 * Converts legacy RootFilter via `migrateLegacyFiltersToV2`. Ensures at least one row.
 */
export function getFiltersForUI(
  stored: FilterV2Root | RootFilter<RunTypes.Key> | null | undefined
): FilterV2Root {
  const emptyV2: FilterV2Root = {
    filterFormat: FILTER_FORMAT_V2,
    filters: [EMPTY_PLACEHOLDER_FILTER],
  };
  if (stored == null) {
    return emptyV2;
  }
  if (isFilterV2(stored)) {
    return stored.filters.length === 0 ? emptyV2 : stored;
  }
  const v2 = migrateLegacyFiltersToV2(stored);
  return v2.filters.length === 0 ? emptyV2 : v2;
}

/** Convert stored filter to FilterV2Root if it isn't already. */
export function ensureFilterV2(stored: StoredFilter): FilterV2Root {
  if (isFilterV2(stored)) {
    return stored;
  }
  return migrateLegacyFiltersToV2(stored);
}

/*
 * Stored-filter helpers
 */

/** Toggle `disabled` on the filter at `index`. */
export function toggleFilterDisabled(
  root: FilterV2Root,
  index: number
): FilterV2Root {
  const item = root.filters[index];
  if (item == null) {
    return root;
  }
  if (isFilterV2Group(item)) {
    return toggleGroupDisabled(root, index);
  }
  return updateFilterV2RootAt(root, index, {
    ...item,
    disabled: !item.disabled,
  });
}

/** Toggle `disabled` on a group at `index` without affecting child filters. */
export function toggleGroupDisabled(
  root: FilterV2Root,
  index: number
): FilterV2Root {
  const item = root.filters[index];
  if (item == null || !isFilterV2Group(item)) {
    return root;
  }
  const updatedGroup: FilterV2Group = {
    ...item,
    disabled: !item.disabled,
  };
  return updateFilterV2RootAt(root, index, updatedGroup as FilterV2Item);
}

/**
 * Upsert a toggle filter: if a filter matching `filter` by key+op+value already
 * exists, flip its `disabled` flag; otherwise append it as enabled.
 *
 * Used for UI toggle filters like "Show only my work" and "Hide crashed runs"
 * that are identified by their content rather than their position.
 */
export function upsertToggleFilter(
  root: FilterV2Root,
  filter: FlatIndividualItem
): FilterV2Root {
  const index = root.filters.findIndex(
    item => !isFilterV2Group(item) && matchesByKeyOpValue(item, filter)
  );
  if (index !== -1) {
    return toggleFilterDisabled(root, index);
  }
  const withConn: FlatIndividualItem =
    root.filters.length > 0 ? {...filter, connector: 'AND'} : filter;
  return {
    filterFormat: FILTER_FORMAT_V2,
    filters: [...root.filters, withConn],
  };
}

/** Check whether two flat items have the same key section+name, op, and value. */
function matchesByKeyOpValue(
  a: FlatIndividualItem,
  b: FlatIndividualItem
): boolean {
  return (
    a.key.section === b.key.section &&
    a.key.name === b.key.name &&
    a.op === b.op &&
    JSON.stringify(a.value) === JSON.stringify(b.value)
  );
}

/**
 * Count active (non-placeholder, non-disabled) conditions in stored filters.
 * Works with both FilterV2Root and legacy RootFilter.
 */
export function activeFilterCountFromStored(
  stored: FilterV2Root | RootFilter<FilterTypes.FilterKey> | null | undefined
): number {
  if (stored == null) {
    return 0;
  }
  if (isFilterV2(stored)) {
    const countNode = (node: FilterV2Root | FilterV2Group): number => {
      let n = 0;
      for (const item of node.filters) {
        if (isFilterV2Group(item)) {
          if (!item.disabled) {
            n += countNode(item);
          }
        } else if (!isEmptyPlaceholderItem(item) && !item.disabled) {
          n += 1;
        }
      }
      return n;
    };
    return countNode(stored);
  }
  return activeFilterCount(stored);
}

/*
 * Freeze-filter helpers
 */

function isFreezeFilter(
  item: FilterTypes.IndividualFilter<FilterTypes.FilterKey>
): boolean {
  const k = item.key;
  return k?.section === 'run' && k?.name === 'createdAt' && item.op === '<=';
}

/**
 * Find the index of a freeze filter (createdAt <=) in stored filters.
 * Works with both formats.
 */
export function findFreezeFilterIndexStored(
  stored: StoredFilter | null | undefined
): number {
  if (stored == null) {
    return -1;
  }
  if (isFilterV2(stored)) {
    return stored.filters.findIndex(
      item => !isFilterV2Group(item) && isFreezeFilter(item)
    );
  }
  if (!stored.filters?.[0]?.filters) {
    return -1;
  }
  return stored.filters[0].filters.findIndex(
    f => isIndividual(f) && isFreezeFilter(f)
  );
}

/**
 * Search `items` for the first non-group filter matching `predicate`,
 * returning both the item and its index.
 */
function findFlatItem(
  items: FilterV2Item[],
  predicate: (item: FlatIndividualItem) => boolean
): {item: FlatIndividualItem; index: number} | null {
  const index = items.findIndex(
    item => !isFilterV2Group(item) && predicate(item)
  );
  if (index === -1) {
    return null;
  }
  return {item: items[index] as FlatIndividualItem, index};
}

/**
 * Add a filter to stored filters.
 *
 * For freeze filters (createdAt <=), updates the existing one in-place
 * rather than adding a duplicate. For all other filters, appends with
 * an AND connector.
 *
 * Accepts legacy RootFilter as input for safety (un-migrated view-spec data),
 * but always returns a FilterV2Root.
 *
 * Used by WBTable's cell-hover "filter by value" shortcut.
 */
export function addFilterFromWBTableToStored(
  stored: StoredFilter | undefined,
  key: RunTypes.Key,
  op: FilterTypes.ValueOp,
  value: RunTypes.Value
): FilterV2Root {
  const v2: FilterV2Root =
    stored == null
      ? EMPTY_STORED_FILTER_V2
      : isFilterV2(stored)
        ? stored
        : migrateLegacyFiltersToV2(stored);

  const newFilter: FlatIndividualItem = {key, op, value};

  if (isFreezeFilter(newFilter)) {
    const found = findFlatItem(v2.filters, isFreezeFilter);
    if (found != null) {
      return updateFilterV2RootAt(
        v2,
        found.index,
        Object.assign({}, found.item, {value, disabled: false})
      );
    }
  }

  return insertFilterV2At(
    v2,
    v2.filters.length,
    v2.filters.length > 0 ? {...newFilter, connector: 'AND'} : newFilter
  );
}
